import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, Star } from "lucide-react";
import { CENTROS, WHATSAPP_URL } from "../../lib/constants";
import MadridMap from "./MadridMap";

export default function Centros() {
    const [active, setActive] = useState(CENTROS[0]);

    return (
        <section
            id="centros"
            data-testid="centros-section"
            className="relative bg-[#050505] py-24 md:py-32 overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                    <div>
                        <span className="font-display tracking-[0.4em] text-xs text-brand-orange">
                            // NUESTROS CENTROS
                        </span>
                        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.9] text-brand-text">
                            ENTRENA DONDE
                            <br />
                            <span className="gold-text">SUCEDEN</span> LAS COSAS.
                        </h2>
                    </div>
                    <p className="max-w-md text-brand-muted text-base">
                        Tres centros ubicados estratégicamente en{" "}
                        <span className="text-brand-text">Madrid Sur</span>.
                        Trabajamos dentro de las instalaciones Basic-Fit con un
                        programa exclusivo F You para llevarte al siguiente
                        nivel.
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 items-start">
                    {/* Interactive Map */}
                    <div className="lg:col-span-7 relative aspect-[4/5] md:aspect-[5/4]">
                        <MadridMap
                            active={active}
                            onSelect={setActive}
                            cities={CENTROS}
                        />
                    </div>

                    {/* Location buttons */}
                    <div className="lg:col-span-5 space-y-4">
                        {CENTROS.map((c, idx) => (
                            <motion.button
                                key={c.id}
                                onClick={() => setActive(c)}
                                data-testid={`centro-card-${c.id}`}
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className={`w-full text-left p-6 md:p-7 border transition-all duration-300 glow-border ${
                                    active.id === c.id
                                        ? "bg-[#1A1A1A] border-brand-orange"
                                        : "bg-[#0F0F0F] border-white/10 hover:border-brand-orange/60"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="font-display text-[10px] tracking-[0.35em] text-brand-orange">
                                                CENTRO 0{idx + 1}
                                            </span>
                                            <span className="h-px w-8 bg-brand-orange" />
                                        </div>
                                        <h3 className="font-display text-3xl md:text-4xl text-brand-text leading-none">
                                            {c.nombre.toUpperCase()}
                                        </h3>
                                        <div className="mt-3 flex items-start gap-2 text-sm text-brand-muted">
                                            <MapPin
                                                size={14}
                                                className="mt-0.5 text-brand-gold shrink-0"
                                            />
                                            <span>
                                                {c.direccion}
                                                <br />
                                                {c.cp}
                                            </span>
                                        </div>
                                    </div>
                                    <ArrowUpRight
                                        className={`shrink-0 transition-transform ${
                                            active.id === c.id
                                                ? "text-brand-orange rotate-0"
                                                : "text-brand-muted"
                                        }`}
                                        size={26}
                                    />
                                </div>

                                <AnimatePresence>
                                    {active.id === c.id && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{
                                                height: "auto",
                                                opacity: 1,
                                            }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <ul className="mt-5 pt-5 border-t border-white/10 space-y-2">
                                                {c.highlights.map((h) => (
                                                    <li
                                                        key={h}
                                                        className="flex items-center gap-3 text-sm text-brand-text"
                                                    >
                                                        <Star
                                                            size={12}
                                                            className="text-brand-gold fill-brand-gold"
                                                        />
                                                        {h}
                                                    </li>
                                                ))}
                                            </ul>
                                            <div className="mt-5 flex flex-wrap gap-3">
                                                <a
                                                    href={WHATSAPP_URL}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`centro-reservar-${c.id}`}
                                                    className="bg-brand-orange hover:bg-brand-orangeHover text-black font-display tracking-wider text-xs px-5 py-3 transition-all"
                                                >
                                                    RESERVAR AQUÍ
                                                </a>
                                                <a
                                                    href={c.maps}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`centro-maps-${c.id}`}
                                                    className="border border-white/20 hover:border-brand-gold hover:text-brand-gold text-brand-text font-display tracking-wider text-xs px-5 py-3 transition-all"
                                                >
                                                    VER EN MAPS
                                                </a>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
